

import { logger } from "../logger/Logger";
import { LightPresets } from "./LightPresets";
import { SceneMath } from "../wgs/scene/SceneMath";
import { CoordinateSystem } from "./CoordinateSystem";
import { UnifiedCamera } from "../tools/UnifiedCamera";

/** @import { Viewer3D } from "./Viewer3D" */
/** @typedef {Viewer3D} Autodesk.Viewing.Viewer3D */

/**
 * Compares a version string (e.g., "3.1") against the current viewerState version.
 * Returns true if version2Compare is greater than or equal the current version.
 *
 * @param {Object} viewerState
 * @param {string} version2Compare
 * @returns {boolean}
 *
 * @private
 */
export function isVersionGreaterThanOrEqual(viewerState, version2Compare) {
  const currentVersion = viewerState?.version || "2.0";

  const parseVersion = (v) => {
    if (typeof v !== 'string') return { major: NaN, minor: NaN };
    const parts = v.split('.');
    const major = parseInt(parts[0], 10);
    const minor = parts.length > 1 ? parseInt(parts[1], 10) : 0;
    return { major, minor };
  };

  const version_current = parseVersion(currentVersion);
  const version_compare = parseVersion(version2Compare);

  if (Number.isNaN(version_current.major) || Number.isNaN(version_compare.major)) {
    return false;
  }

  if (version_compare.major !== version_current.major) {
    return version_current.major > version_compare.major;
  }

  if (Number.isNaN(version_current.minor) || Number.isNaN(version_compare.minor)) {
    return false;
  }

  return version_current.minor >= version_compare.minor;
}

/**
 * All-inclusive filter constant used when no filter is provided.
 * @type {boolean}
 * @private
 */
const _FILTER_ALL = true;

/**
 * Class for creating and restoring viewer states.
 *
 * Main interactions come from methods
 * - {@link Autodesk.Viewing.Private.ViewerState#getState}
 * - {@link Autodesk.Viewing.Private.ViewerState#restoreState}
 * @tutorial viewer_state
 * @param {Autodesk.Viewing.Viewer3D} viewer - Viewer instance used to operate on.
 * @private
 */
export class ViewerState {

  /**
   * Coordinate System Space, used to transform coordinates from/to aggregated coordinate space
   * @type{CoordinateSystem}
   * @private
   */
  cs = null;

  constructor(viewer) {
    this._viewer = viewer;
  }

  /**
   * Get a model object (that is visible) from the passed-in seedUrn.
   *
   * @returns {Autodesk.Viewing.Model} - Model that is associated with the seedUrn.
   * @private
   */
  getVisibleModel(seedUrn) {
    const visibleModels = this._viewer.getVisibleModels();
    for (let i = 0; i < visibleModels.length; ++i) {
      const model = visibleModels[i];
      if (this.getSeedUrn(model) === seedUrn) {
        return model;
      }
    }
  }

  /**
   * Adds information about visibility, selection, and explosion view, per visible model.
   *
   * @param {object} viewerState - the state object to add the information to
   * @private
   */
  createObjectSets(viewerState) {
    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");

    const propName = useV3Schema ? 'overrides' : 'objectSet';
    let objectSet = viewerState[propName];
    if (!Array.isArray(objectSet)) {
      viewerState[propName] = objectSet = [];
    }

    const visibleModels = this._viewer.getVisibleModels();
    for (let i = 0; i < visibleModels.length; i++) {
      const model = visibleModels[i];

      if (useV3Schema && model.is2d()) {
        continue;
      }

      const selectedNodes = this.getSelectedNodes(model);
      const seedUrn = this.getSeedUrn(model);

      if (useV3Schema) {
        const node = model.getDocumentNode();
        const viewableGuid = this.getViewableGuid(model);
        objectSet[i] = {
          versionUrn: this.getVersionUrn(model),
          lineageUrn: this.getLineageUrn(model),
          viewable: {
            viewableGuid: viewableGuid,
            viewableName: node ? node.name() : ''
          },
          visibility: {
            'defaultNodeVisibility': model.visibilityManager?.getDefaultVisibility(),
            'overridesOrderedByHierarchy': true, // Overrides are ordered by model hierarchy, i.e., children only be listed after their parents.
            'overrides': model.visibilityManager?.getVisibility()
          }
        };

        // only add viewableID if it's not empty - otherwise causing bugs with viewpoint creation (VIZX-2937)
        const viewableID = node ? node.data?.viewableID : '';
        if (viewableID !== '') {
          objectSet[i].viewable.viewableId = viewableID;
        }

        if (selectedNodes.length > 0) {
          objectSet[i].visibility.selected = selectedNodes;
        }
      } else {
        objectSet[i] = {
          id: selectedNodes, // Works for 2d and 3d
          idType: 'lmv'
        };

        if (visibleModels.length > 1) {
          objectSet[i].seedUrn = seedUrn;
        }

        // Spec call for these elements to grouped in an Object at an Array's index 0.
        // 3d models attributes
        if (!model.is2d()) {
          objectSet[i].isolated = this._viewer.getIsolatedNodes(model);
          objectSet[i].hidden = this._viewer.getHiddenNodes(model);
          objectSet[i].explodeScale = this._viewer.getExplodeScale();
          objectSet[i].explodeOptions = this._viewer.getExplodeOptions();
        } else {
          // 2d models attributes
          objectSet[i].isolated = this.getVisibleLayers2d(model);
          objectSet[i].allLayers = this.areAllLayersVisible(model);
          objectSet[i].hidden = []; // There's no hide feature for 2d.
        }
      }
    }
  }

  /**
   * Returns the given string if it is a valid schema version following the pattern "^\d+\.\d+$",
   * and the default version otherwise.
   * @param {String} schemaVersion
   * @returns {String}
   * @private
   */
  checkVersion(schemaVersion) {
    const valid = /^\d+\.\d+$/.test(schemaVersion);
    if (!valid) {
      return "2.0";
    }
    return schemaVersion;
  }

  /**
   * Returns the decoded version URN of the given model if available. SeedUrn otherwise.
   * Note the version URN is different for each version of the model.
   *
   * @param {Model} model
   * @returns {String} - Decoded version URN if available. Empty string otherwise.
   * @private
   */
  getVersionUrn(model) {
    let urn = model.getDocumentNode()?.getRootNode()?.urn();
    if (!urn) {
      console.warn('Could not find valid Manifest node to receive VersionUrn.');
      urn = model.getSeedUrn();
    }
    const versionUrn = Autodesk.Viewing.tryFromUrlSafeBase64(urn);
    return versionUrn;
  }

  /**
   * Returns the lineage URN of the given model if available. SeedUrn otherwise.
   * Note the lineage URN stays the same across different versions of the model.
   *
   * @param {Model} model
   * @returns {String} - Decoded version URN if available. Empty string otherwise.
   * @private
   */
  getLineageUrn(model) {
    const node = model.getDocumentNode();
    let lineageUrn = node ? node.lineageUrn() : '';
    if (!lineageUrn || lineageUrn === '') {
      console.warn('Could not find valid Manifest node to receive LineageUrn.');
      const seedUrn = model.getSeedUrn();
      lineageUrn = Autodesk.Viewing.tryFromUrlSafeBase64(seedUrn);
    }
    return lineageUrn;
  }

  /**
   * Returns the viewable Guid of the given model if available. Empty string otherwise.
   * Note each model should have at least 1 viewable that contains geometry.
   *
   * @param {Model} model
   * @returns {String} - Viewable Guid if available. Empty string otherwise.
   * @private
   */
  getViewableGuid(model) {
    const node = model.getDocumentNode();
    if (!node) {
      return '';
    }
    return node.guid();
  }

  /**
   * Finds the model to apply the visibility overrides to, given the 'override' portion of v3 ViewerState.
   * @param {Object} override
   * @returns {Model}
   * @private
   */
  findModelToApplyOverrides(override) {
    const lineageUrn = override.lineageUrn;
    const viewableGuid = override.viewable.viewableGuid;
    const visibleModels = this._viewer.getVisibleModels();

    // Required to support looked-back viewpoints (VIZX-2962):
    // Given a model with 2 versions v1 and v2,
    // and 2 viewpoints, with 1 viewpoint created in v1, and the other created in v2,
    // both should be applicable if the model is opened in v2.
    // Note that is why we do not check for version URN here, as we would miss applying older Viewpoints otherwise.
    const foundModels = visibleModels.filter((model) => {
      const matchLineageUrn = this.getLineageUrn(model) === lineageUrn;
      return matchLineageUrn;
    });

    // Support copy promotion in case we found exactly 1 match:
    // When copying a model folder, viewpoints are copied as well. However, it may happen that viewableGuids change.
    // Therefore, we do not check for viewables to match here.
    if (foundModels.length === 1) {
      return foundModels[0];
    }

    if (foundModels.length === 0) {
      console.warn("Could not find model to apply ViewerState v3 overrides:\n" + lineageUrn + "\n" + viewableGuid);
      return undefined;
    }

    // Multiple models were found with the same lineage URN.
    const foundViewables = visibleModels.filter((model) => {
      const matchViewable = this.getViewableGuid(model) === viewableGuid;
      return matchViewable;
    });

    if (foundViewables.length === 1) {
      return foundViewables[0];
    }

    if (foundViewables.length === 0) {
      console.warn("Could not find Viewable to apply ViewerState v3 overrides:\n" + lineageUrn + "\n" + viewableGuid);
      return undefined;
    }

    // multiple viewables with the same guid have been found
    console.warn("Could not find unambiguous Viewable to apply ViewerState v3 overrides:\n" + lineageUrn + "\n" + viewableGuid);
    return undefined;
  }

  /**
   * Returns a viewer state Object for the current viewer instance.
   *
   * @param {object} filter - Object with a structure similar to the output where
   *                          values are replaced with Booleans true/false indicating
   *                          whether they should be stored or not.
   * @returns {object} Plain object describing the state of the viewer.
   * @tutorial viewer_state
   */
  getState(filter, schemaVersion = "2.0", coordinateSystem = "") {

    const viewerState = {};

    // add version info
    viewerState["version"] = this.checkVersion(schemaVersion);

    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");

    if (!useV3Schema) {
      // Adding level-0 properties
      viewerState["seedURN"] = this.getSeedUrn();
    }

    // Add coordinate system info and setup transform to aggregated coordinate space.
    this.addCoordinateSystem(viewerState, coordinateSystem);

    // Create an objectSet for each model.
    this.createObjectSets(viewerState);

    // Viewport
    this.addViewport(viewerState);

    // Autocam
    if (!useV3Schema) {
      viewerState["autocam"] = {
        sceneUpDirection: this._viewer.autocam.sceneUpDirection.clone(),
        sceneFrontDirection: this._viewer.autocam.sceneFrontDirection.clone(),
        cubeFront: this._viewer.autocam.cubeFront.clone()
      };
    }

    // Render Options
    this.addRenderOptions(viewerState);

    // Section Planes Rendering State
    this.addSectionPlanes(viewerState);

    // Allow extensions to inject their state data
    // Autodesk.AEC.LevelsExtension
    const useV2Schema = this.getVersion(viewerState) === "2.0";
    if (useV2Schema) {
      for (let extensionName in this._viewer.loadedExtensions) {
        const extension = this._viewer.loadedExtensions[extensionName];
        extension.getState && extension.getState(viewerState);
      }
    }

    // For v3 schema, we only allow the levels extension to add the floor guid if any.
    // Other custom properties are not v3 compliant for the moment, and would fail
    // validation against the scribble backend.
    if (useV3Schema) {
      const extension = this._viewer.getExtension('Autodesk.AEC.LevelsExtension');
      extension?.getState && extension?.getState(viewerState);
    }

    // Filter out values the user doesn't want to consume before returning.
    if (filter && filter !== _FILTER_ALL) {
      this.applyFilter(viewerState, filter);
    }

    // Tool states
    if (isVersionGreaterThanOrEqual(viewerState, "3.1")) {
      this.#getToolStates(viewerState);
    }

    return viewerState;
  }

  /**
   * Returns the version info for this viewer state object.
   * Default is "2.0".
   * @param {Oject} viewerState - The JSON state object
   * @returns {String} - a String following the pattern "^\d+\.\d+$"
   * @private
   */
  getVersion(viewerState) {
    const version = viewerState.version;
    if (!version) {
      return "2.0";
    }
    return version;
  }

  /**
   * Adds render options to the state.
   *
   * @param {ViewerState} viewerState - the state to add render options to.
   */
  addRenderOptions(viewerState) {
    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");
    if (useV3Schema) {
      return;
    }

    let renderOptions = viewerState["renderOptions"];
    if (!renderOptions) {
      renderOptions = viewerState["renderOptions"] = {};
    }
    renderOptions["environment"] = this.getEnvironmentName();
    renderOptions["ambientOcclusion"] = {
      enabled: this._viewer.impl.renderer().getAOEnabled(),
      radius: this._viewer.impl.renderer().getAORadius(),
      intensity: this._viewer.impl.renderer().getAOIntensity()
    };
    renderOptions["toneMap"] = {
      method: this._viewer.impl.renderer().getToneMapMethod(),
      exposure: this._viewer.impl.renderer().getExposureBias(),
      lightMultiplier: this.getToneMapIntensity()
    };
    renderOptions["appearance"] = {
      ghostHidden: this._viewer.impl.showGhosting,
      ambientShadow: this._viewer.prefs.get('ambientShadows'),
      antiAliasing: this._viewer.impl.renderer().settings.antialias,
      progressiveDisplay: this._viewer.prefs.get('progressiveRendering'),
      swapBlackAndWhite: this._viewer.prefs.get('swapBlackAndWhite'),
      displayLines: this._viewer.prefs.get('lineRendering'),
      displayPoints: this._viewer.prefs.get('pointRendering')
    };
  }

  #getToolStates(viewerState) {
    const tools = viewerState["tools"] = {};

    // Section Tool State
    // https://git.autodesk.com/BIM360/visualization-schemas/blob/main/autodesk.viewer_section-1.0.0.yaml
    const section = this._viewer.getExtension('Autodesk.SmartSection');
    section?.getToolState(tools, "1.0.0", this.cs);
  }

  /**
   * Add cutplanes (aka: Sectioning) to the state if any, for ViewerState 2.0 and 3.0.
   * Only valid for 3d models.
   *
   * @param {ViewerState} viewerState - the state to add section planes (or cutplanes) to.
   * @private
   */
  addSectionPlanes(viewerState) {
    if (this._viewer.impl.is2d) {
      return;
    }

    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");

    const propName = useV3Schema ? "sectionPlanes" : "cutplanes";
    let sectionPlanes = viewerState[propName];
    if (!sectionPlanes) {
      sectionPlanes = viewerState[propName] = [];
    }

    const isSmartSectionEnabled = !!this._viewer.getExtension('Autodesk.SmartSection');
    if (!isSmartSectionEnabled) {
      this.#addSectionPlanesForOldSectionTool(viewerState, sectionPlanes);
      return;
    }

    if (this.getVersion(viewerState) === "2.0") {
      const section = this._viewer.getExtension('Autodesk.SmartSection');
      section?.toViewerStateV2(viewerState);
    }

    if (isVersionGreaterThanOrEqual(viewerState, "3.1")) {
      const section = this._viewer.getExtension('Autodesk.SmartSection');
      section?.getRenderingState(sectionPlanes, "1.0.0", this.cs);
    }
  }

  /**
   * Deprecated: Add section data from old Section Tool ('Autodesk.Section').
   * Will be replaced with the SmartSection Tool ('Autodesk.SmartSection').
   *
   * @param {Object} viewerState
   * @param {Object} sectionPlanes
   */
  #addSectionPlanesForOldSectionTool(viewerState, sectionPlanes) {
    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");

    // will be deprecated: old section tool
    const planes = this._viewer.impl.getCutPlaneSet('__set_view');
    if (useV3Schema) {
      // For V3 we skip getState of extensions, so we add the section tool cutplanes here
      planes.push(...this._viewer.impl.getCutPlaneSet('Autodesk.Viewing.Extension.Section.SectionTool'));
      this.cs.transformSectionPlanesToCS(planes);
    }

    planes.forEach((plane) => sectionPlanes.push(plane.toArray()));
  }

  /**
   * Adds the field of view to the viewport state object.
   * Either in DEG or RAD depending on the schema version.
   */
  #addFieldOfView(useV3Schema, viewport) {
    const nav = this._viewer.navigation;

    if (!nav.getCamera().isPerspective) {
      return;
    }

    let fov = nav.getVerticalFov();

    const minFovDeg = nav.getFovMin();
    const maxFovDeg = nav.getFovMax();

    // guards for invalid fov in DEG
    if (typeof fov !== 'number' || !Number.isFinite(fov)) {
      fov = UnifiedCamera.ORTHO_FOV;
      console.warn(`Invalid fov in DEG. Clamping to UnifiedCamera.ORTHO_FOV (${UnifiedCamera.ORTHO_FOV} degrees).`);
    }
    fov = Math.min(Math.max(fov, minFovDeg), maxFovDeg);

    if (useV3Schema) {
      fov = THREE.Math.degToRad(fov);
    }

    viewport["fieldOfView"] = fov;
  }

  /**
   * Restores fieldOfView from the viewport state object.
   * For ViewerState v3+, viewport.fieldOfView is expected in radians and is converted to degrees for Navigation/UnifiedCamera.
   * For legacy schemas, viewport.fieldOfView is expected in degrees.
   *
   * @returns {number} fov in degrees (clamped to Navigation's min/max).
   */
  #restoreFieldOfView(useV3Schema, viewport) {
    const nav = this._viewer.navigation;

    const minFovDeg = nav.getFovMin();
    const maxFovDeg = nav.getFovMax();

    let fov;
    if ("fieldOfView" in viewport) {
      fov = parseFloat(viewport.fieldOfView);
      if (useV3Schema) {
        fov = THREE.Math.radToDeg(fov);
      }
    } else {
      fov = nav.getVerticalFov();
    }

    // guards for invalid fov in DEG
    if (typeof fov !== 'number' || !Number.isFinite(fov)) {
      fov = UnifiedCamera.ORTHO_FOV;
      console.warn(`Invalid fieldOfView value. Clamping to UnifiedCamera.ORTHO_FOV (${UnifiedCamera.ORTHO_FOV} degrees).`);
    }

    // final clamp in degrees
    fov = Math.min(Math.max(fov, minFovDeg), maxFovDeg);
    return fov;
  }

  /**
   * Adds the viewport to the given state object.
   * Reference: https://git.autodesk.com/BIM360/visualization-schemas/blob/60e6c19b6da27bd4541cc721518f004699eae59e/autodesk.viewer_state-3.0.0.yaml#L18
   * @param {Object} viewerState - The parent JSON object to add the viewport state to.
   */
  addViewport(viewerState) {
    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");

    const propName = useV3Schema ? "camera" : "viewport";
    let viewport = viewerState[propName];
    if (!viewport) {
      viewport = viewerState[propName] = {};
    }

    const nav = this._viewer.navigation;
    const position = nav.getPosition();
    const target = nav.getTarget();
    const up = nav.getCameraUpVector();
    const pivot = nav.getPivotPoint();
    const worldUp = nav.getWorldUpVector();

    const isPerspectiveCam = nav.getCamera().isPerspective;
    viewport["aspectRatio"] = this.getAspectRatio();
    viewport["isOrthographic"] = !isPerspectiveCam;

    if (useV3Schema) {
      this.cs.transformCameraToCS(position, target, up, worldUp, pivot);

      viewport["position"] = position.toArray();
      viewport["rotation"] = SceneMath.getCameraRotationAsQuat(position, target, up).toArray();
      const distanceToTarget = SceneMath.getCameraTargetDistance(position, target);
      viewport["distanceToTarget"] = distanceToTarget > 0 ? distanceToTarget : 1;
      viewport["worldUp"] = worldUp.toArray();
      viewport["pivotPoint"] = pivot.toArray();
      if (isPerspectiveCam) {
        this.#addFieldOfView(useV3Schema, viewport);
      } else {
        const orthographicHeight = this.getOrthographicHeight();
        viewport["orthographicHeight"] = this.cs ?
        this.cs.transformOrthographicHeightFromCS(orthographicHeight) :
        orthographicHeight;
      }
    } else {
      viewport["name"] = ""; // TODO: Populate accordingly; Requested by the mobile team.
      viewport["eye"] = position.toArray();
      viewport["target"] = target.toArray();
      viewport["up"] = up.toArray();
      viewport["distanceToOrbit"] = nav.getPivotPlaneDistance();
      viewport["projection"] = isPerspectiveCam ? "perspective" : "orthographic";
      viewport["worldUpVector"] = worldUp.toArray();
      viewport["pivotPoint"] = pivot.toArray();

      if (isPerspectiveCam) {
        this.#addFieldOfView(useV3Schema, viewport);
      } else {
        viewport["orthographicHeight"] = this.getOrthographicHeight();
      }
    }
  }

  /**
   * Adds coordinate system information, according to v3 schema.
   * Transform contained coordinates from/to aggregated coordinate space if required.
   *
   * @param {object} viewerState - the state object to add the information to
   * @param {String} coordinateSystem - the coordinateSystem to add
   * @private
   */
  addCoordinateSystem(viewerState, coordinateSystem) {
    const isCoordinateSystemSupplied = coordinateSystem && coordinateSystem !== '';

    if (!isVersionGreaterThanOrEqual(viewerState, "3.0")) {
      this.cs = null;

      if (isCoordinateSystemSupplied) {
        console.error('ViewerState schema version is < 3.0, coordinate system information will be ignored.');
      }
      return;
    }

    if (!isCoordinateSystemSupplied) {
      coordinateSystem = JSON.stringify({ version: 1, context: { type: 'Model' } });
    }

    // add coordinate system info to state
    viewerState.coordinateSystem = JSON.parse(coordinateSystem);
    const context = viewerState.coordinateSystem.context;

    // Use model space.
    // In case of 1 model, we can add more model information.
    const visibleModels = this._viewer.getVisibleModels();
    if (context.type === 'Model') {
      if (visibleModels?.length === 1) {
        const model = visibleModels[0];
        context.versionUrn = this.getVersionUrn(model);
        context.lineageUrn = this.getLineageUrn(model);
      } else if (visibleModels?.length > 1) {
        console.warn('Unexpected "Model" context for a multi-model view');
      }
    }

    // Setup transformations for either aggregated space or single model space.
    // Can be applied for a single model or multiple models.
    const model = this._viewer.getFirstModel();
    this.cs = new CoordinateSystem(model, context.type);
  }

  /**
   * Restores all of the object sets. Sets the state of each model (selected, hidden, isolated nodes).
   * @param {object} viewerState - see viewerState.getState()
   * @private
   */
  restoreObjectSet(viewerState) {
    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");

    const objectSets = useV3Schema ? viewerState.overrides : viewerState.objectSet;
    if (!Array.isArray(objectSets) || objectSets.length === 0) {
      return;
    }

    const selectAggregate = [];
    const isolateAndHideAggregate = [];

    if (useV3Schema) {
      objectSets.forEach((objectSet) => {
        const model = this.findModelToApplyOverrides(objectSet);
        if (model) {
          const visibility = objectSet.visibility;
          model.visibilityManager.setVisibility(
            visibility.defaultNodeVisibility,
            visibility.overrides,
            visibility.overridesOrderedByHierarchy);

          const selection = objectSet.visibility.selected ? objectSet.visibility.selected : [];
          selectAggregate.push({ model: model, ids: selection });
        } else {
          console.warn("Could not find model to apply visibility overrides. Skipping it.");
        }
      });
    } else {
      // Legacy-fallback
      var model = this._viewer.model;
      for (var i = 0; i < objectSets.length; ++i) {
        var objectSet = objectSets[i];

        // Ensure that the objectSet was generated by the getState function
        if (objectSet.idType !== "lmv") {
          continue;
        }
        var seedUrn = objectSet.seedUrn;
        if (seedUrn) {
          model = this.getVisibleModel(seedUrn);
          // the model is not loaded
          if (!model) continue;
        }

        // Selection (2d and 3d)
        let selectionIds = objectSet.id;
        if (selectionIds) {
          selectionIds = this.toIntArray(selectionIds);
          selectAggregate.push({ model: model, ids: selectionIds });
        }

        // Isolation / Hidden depends on whether it is 2d or 3d
        if (model.is2d()) {
          // TODO: aggregate layers are not yet supported.
          // 2d Isolation is Layer visibility
          var visibleLayers = objectSet.isolated;
          if (Array.isArray(visibleLayers) && visibleLayers.length > 0) {
            // Only certain layers are visible
            this._viewer.setLayerVisible(null, false); // start by hiding all

            // Make sure layers integer-types
            for (var k = 0; k < visibleLayers.length; ++k) {
              visibleLayers[k] = parseInt(visibleLayers[k]);
            }
            this._viewer.impl.setLayerVisible(visibleLayers, true);
          } else if (!objectSet.allLayers) {
            // If there are no isolated ids, that means that all of the layers should be invisible.
            // Reported in LMV-3537
            this._viewer.setLayerVisible(null, false);
          }
        } else {
          // 3d Isolation
          const isolatedIds = this.toIntArray(objectSet.isolated);
          const hiddenIds = this.toIntArray(objectSet.hidden);
          isolateAndHideAggregate.push({ model, isolatedIds, hiddenIds });
        }

        // Explode scale (3d)
        if ("explodeScale" in objectSet) {
          var explodeScale = parseFloat(objectSet.explodeScale);
          var explodeOptions = objectSet.explodeOptions;
          if (this._viewer.explode) {
            this._viewer.explode(explodeScale, explodeOptions);
          }
        }
      }
    }

    if (selectAggregate.length > 0) {
      this._viewer.impl.selector.setAggregateSelection(selectAggregate);
    }

    if (isolateAndHideAggregate.length > 0) {
      this._viewer.impl.visibilityManager.aggregateRestore(isolateAndHideAggregate);
    }
  }

  /**
   * Restores the associated viewer instance with the provided viewerState object.
   *
   * @param {object} viewerState
   * @param {object} [filter] - Similar in structure to viewerState used to filter out values
   * that should not be restored.
   * @param {boolean} [immediate] - Whether the state should be apply with (false)
   * or without (true) a smooth transition.
   * @returns {boolean} True if the operation was successful.
   * @tutorial viewer_state
   */
  restoreState(viewerState, filter, immediate) {
    if (!viewerState) {
      logger.warn("restoreState has no viewer state to restore from.");
      return false;
    }

    if (!this._viewer || !this._viewer.model) {
      logger.warn("restoreState has no viewer or model to restore.");
      return false;
    }

    if (filter && filter !== _FILTER_ALL) {
      // To avoid modifying viewerState passed in, we create a clone of it
      viewerState = JSON.parse(JSON.stringify(viewerState));
      this.applyFilter(viewerState, filter);
    }

    // Create coordinate system if v3 schema is used and coordinate system is defined in shared and single model context.
    if (isVersionGreaterThanOrEqual(viewerState, "3.0")) {
      const model = this._viewer.getFirstModel();
      const type = viewerState.coordinateSystem.context.type;
      this.cs = new CoordinateSystem(model, type);
    }

    // Objectset
    this.restoreObjectSet(viewerState);

    // AutoCam
    const autocamState = viewerState.autocam;
    if (autocamState) {
      const autocam = this._viewer.autocam;
      autocam.sceneUpDirection.copy(autocamState.sceneUpDirection);
      autocam.sceneFrontDirection.copy(autocamState.sceneFrontDirection);
      autocam.cubeFront.copy(autocamState.cubeFront);
      autocam.camera.worldup.copy(autocamState.sceneUpDirection);
      autocam.cameraChangedCallback(true);
    }

    // Viewport
    this.restoreViewport(viewerState, immediate);

    // Render option state
    this.restoreRenderOptions(viewerState);

    // Section Planes Rendering State
    this.restoreSectionPlanes(viewerState);

    // Allow extensions to restore their data
    for (var extensionName in this._viewer.loadedExtensions) {
      var extension = this._viewer.loadedExtensions[extensionName];
      extension.restoreState && extension.restoreState(viewerState, immediate);
    }

    if (isVersionGreaterThanOrEqual(viewerState, "3.1")) {
      this.#restoreToolStates(viewerState);
    }

    return true;
  }

  #restoreToolStates(viewerState) {
    const tools = viewerState["tools"];
    if (!tools) {
      return;
    }

    // See below https://jira.autodesk.com/browse/VIZX-4470
    if (viewerState.sectionPlanes.length > 0 && !tools["autodesk.viewer:section-1.0.0"]) {
      return;
    }

    // Section Tool State
    const section = this._viewer.getExtension('Autodesk.SmartSection');
    section?.restoreToolState(tools, "1.0.0", this.cs);
  }

  /**
   * Restore cutplanes (aka: Sectioning) data only for 3d models.
   *
   * @param {ViewerState} viewerState
   * @private
   */
  restoreSectionPlanes(viewerState) {
    const is3d = !this._viewer.impl.is2d;
    if (!is3d) {
      return;
    }

    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");
    const sectionPlanes = useV3Schema ? viewerState.sectionPlanes : viewerState.cutplanes;
    if (!sectionPlanes) {
      return;
    }

    if (!Array.isArray(sectionPlanes)) {
      return;
    }

    const cutplanes = [];
    sectionPlanes.forEach((plane) => {
      if (Array.isArray(plane) && plane.length >= 4) {
        cutplanes.push(new THREE.Vector4(plane[0], plane[1], plane[2], plane[3]));
      }
    });

    const removeUnneededCutplanes = (viewerState, planes) => {
      const supportsLevels = !!this._viewer.getExtension('Autodesk.AEC.LevelsExtension');
      if (supportsLevels && viewerState.floorGuid === undefined) {
        // Legacy fallback for old serialized viewer states.
        // TODO: Added on February 2019. Can be removed after a while when we can assume that all issues are serialized with new serialization.
        //
        // Previously the cutplanes were saved as is, together with the section cutplanes.
        // This created problems when restoring a state because there was no way to differentiate among
        // the different sets. Instead of saving the planes we now save the floor guid, but for states
        // that were saved between the time the levels extension was introduced, and when the floorGuid
        // attribute was added to the state, we need to remove the extraneous planes from the __set_view set.
        // Heuristic:
        // Remove from __set_view if exists, and there are 3 or 8 cutplanes, and the last two have the shape of
        // [new THREE.Vector4(0,0,-1,zMin), new THREE.Vector4(0,0,1,-zMax)].
        // Reasoning: Revit files (the only type that support levels) can have only 1 or 6 planes, from the Section
        // extension, so the extra 2 probably come from the level extension. We check the shape to be sure.
        const length = planes.length;
        if (length === 3 || length === 8) {
          const beforeLast = planes[length - 2];
          const last = planes[length - 1];
          if (beforeLast.x === 0 && beforeLast.y === 0 && beforeLast.z === -1 &&
          last.x === 0 && last.y === 0 && last.z === 1) {
            planes.splice(-2, 2); // Remove last 2 elements
          }
        }
      }
    };

    removeUnneededCutplanes(viewerState, cutplanes);

    if (useV3Schema) {
      this.cs.transformSectionPlanesFromCS(cutplanes);
    }

    const isSmartSectionEnabled = !!this._viewer.getExtension('Autodesk.SmartSection');
    if (isSmartSectionEnabled) {
      const section = this._viewer.getExtension('Autodesk.SmartSection');
      if (this.getVersion(viewerState) === "3.0") {
        section.fromViewerStateV3(cutplanes);
      }

      if (this.getVersion(viewerState) === "2.0") {
        section.fromViewerStateV2(viewerState);
      }

      if (isVersionGreaterThanOrEqual(viewerState, "3.1")) {
        // This is a workaround for https://jira.autodesk.com/browse/VIZX-4470
        // V3.1+ states shouldn't have old section tool data but when they do
        // we can assume that it is meant as V3.0
        if (cutplanes.length > 0 && !viewerState.tools?.["autodesk.viewer:section-1.0.0"]) {
          section.fromViewerStateV3(cutplanes);
        }
      }

      return;
    }

    this._viewer.impl.setCutPlaneSet('__set_view', cutplanes);

    // TODO: This is a workaround so that the restored cutplanes are also used by the edge material.
    // https://jira.autodesk.com/browse/VIZX-3137
    //
    // Calling `setCutPlaneSetFor2DRendering()` only changes the ordering of cutplanes,
    //     so that they are added in front of all other cutplanes,
    //     similar to the sections tool, which uses the same function.
    // This is currently required when using leechviewers (e.g. with minimaps / top view renderer),
    //     as cutplanes for some materials (e.g. edge materials) are not updating correctly between leech viewers.
    if (useV3Schema) {
      this._viewer.impl.setCutPlaneSetFor2DRendering('__set_view');
    }
  }

  /**
   * Removes sectioning parameters from the viewer state.
   * @param {ViewerState} viewerState
   */
  removeSectioning(viewerState) {
    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");
    if (!useV3Schema) {
      return;
    }

    delete viewerState.sectionPlanes;

    if (isVersionGreaterThanOrEqual(viewerState, "3.1") && viewerState.tools["autodesk.viewer:section-1.0.0"]) {
      delete viewerState.tools["autodesk.viewer:section-1.0.0"];
    }
  }

  /**
   * Restores the viewport given in ViewerState object to the current camera.
   *
   * @param {object} viewerState - the viewer state containing the viewport to restore the camera from
   * @param {boolean} [immediate] - Whether the state should be applied immediately (true) or with an animation (false)
   */
  restoreViewport(viewerState, immediate) {
    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");

    const viewport = useV3Schema ? viewerState.camera : viewerState.viewport;
    if (!viewport) {
      return;
    }

    const nav = this._viewer.navigation;

    let target;
    let up;
    let eye;
    let worldUp;
    let fov;
    let pivot;
    let orthoScale = this.getOrthographicHeight();
    if (useV3Schema) {
      eye = this.getVector3FromArray(viewport.position, nav.getPosition());
      pivot = this.getVector3FromArray(viewport.pivotPoint, nav.getPivotPoint());

      const rotation = this.getQuaternionFromArray(viewport.rotation, new THREE.Quaternion());

      // get target point
      const distanceToTarget = Number(viewport.distanceToTarget);
      const dirDefault = new THREE.Vector3(0, 0, -1);
      const upDefault = new THREE.Vector3(0, 1, 0);
      const dir = dirDefault.clone().applyQuaternion(rotation).normalize();

      target = eye.clone().add(dir.multiplyScalar(distanceToTarget));
      up = upDefault.applyQuaternion(rotation).normalize();
      worldUp = this.getVector3FromArray(viewport.worldUp, null);

      this.cs.transformCameraFromCS(eye, target, up, worldUp, pivot);

      fov = this.#restoreFieldOfView(useV3Schema, viewport);

      if ('orthographicHeight' in viewport) {
        orthoScale = this.cs.transformOrthographicHeightFromCS(Number(viewport.orthographicHeight));
      }
    } else {
      eye = this.getVector3FromArray(viewport.eye, nav.getPosition());
      up = this.getVector3FromArray(viewport.up, nav.getCameraUpVector());
      target = this.getVector3FromArray(viewport.target, nav.getTarget());
      worldUp = this.getVector3FromArray(viewport.worldUpVector, null);
      fov = this.#restoreFieldOfView(useV3Schema, viewport);
      pivot = this.getVector3FromArray(viewport.pivotPoint, nav.getPivotPoint());
      if ('orthographicHeight' in viewport) {
        orthoScale = Number(viewport.orthographicHeight);
      }
    }

    if (!worldUp) {
      const model = this._viewer.getFirstModel();
      const upVectorArray = model?.getUpVector();
      if (upVectorArray) {
        worldUp = new THREE.Vector3().fromArray(upVectorArray);
      } else {
        worldUp = new THREE.Vector3(0, 1, 0); // TODO: Can we do better? Is it worth it?
      }
    }

    // Retain current values if not available in restore object
    let isPerspective = nav.getCamera().isPerspective;
    if ('isOrthographic' in viewport) {
      isPerspective = !viewport.isOrthographic;
    }

    const camera = {
      position: eye,
      target: target,
      up: up,
      worldup: worldUp,
      aspect: this._viewer.impl.camera.aspect,
      fov: fov,
      orthoScale: orthoScale,
      isPerspective: isPerspective,
      pivot: pivot
    };

    this.restoreCameraState(camera, immediate);
  }

  /**
   * Restore render options.
   *
   * @param {ViewerState} viewerState
   * @private
   */
  restoreRenderOptions(viewerState) {
    const useV3Schema = isVersionGreaterThanOrEqual(viewerState, "3.0");
    if (useV3Schema) {
      return;
    }

    const is3d = !this._viewer.impl.is2d;

    const renderOptions = viewerState.renderOptions;
    if (!renderOptions) {
      return;
    }

    // current values
    const renderer = this._viewer.impl.renderer();
    const prefs = this._viewer.prefs;
    let saoEnabled = prefs.get('ambientShadows');
    let antiAliasing = prefs.get('antialiasing');

    const sao = renderOptions.ambientOcclusion;
    if (sao) {
      if ("enabled" in sao) {
        saoEnabled = sao.enabled;
      }
      const saoRadius = "radius" in sao ? sao.radius : null;
      const saoIntensity = "intensity" in sao ? sao.intensity : null;
      if (saoRadius !== null && saoIntensity !== null) {
        if (saoRadius !== renderer.getAORadius() ||
        saoIntensity !== renderer.getAOIntensity()) {
          renderer.setAOOptions(saoRadius, saoIntensity);
          renderer.composeFinalFrame();
        }
      }
    }

    if ("environment" in renderOptions) {
      const lightPresetIndex = this.getLightPresetIndex(renderOptions.environment);
      if (lightPresetIndex !== -1 && lightPresetIndex !== prefs.get('lightPreset') && is3d) {
        this._viewer.setLightPreset(lightPresetIndex);
      }
    }

    // ToneMap values are overrides to the environment settings.
    const toneMap = renderOptions.toneMap;
    if (toneMap) {
      let invalidate = false;
      const exposure = "exposure" in toneMap ? toneMap.exposure : null;
      const toneMapIntensity = "lightMultiplier" in toneMap ? toneMap.lightMultiplier : null;

      if (exposure !== null && exposure !== renderer.getExposureBias()) {
        renderer.setTonemapExposureBias(exposure);
        invalidate = true;
      }

      if (toneMapIntensity !== null && this._viewer.impl.dir_light1 && toneMapIntensity !== this.getToneMapIntensity()) {
        this._viewer.impl.dir_light1.intensity = Math.pow(2.0, toneMapIntensity);
        invalidate = true;
      }

      if (invalidate) {
        this._viewer.impl.invalidate(true);
      }
    }

    const appearance = renderOptions.appearance;
    if (appearance) {
      if ("antiAliasing" in appearance) {
        antiAliasing = appearance.antiAliasing;
      }
      if ("progressiveDisplay" in appearance && appearance.progressiveDisplay !== prefs.get('progressiveRendering')) {
        this._viewer.setProgressiveRendering(appearance.progressiveDisplay);
      }
      if ("swapBlackAndWhite" in appearance && appearance.swapBlackAndWhite !== prefs.get('swapBlackAndWhite')) {
        this._viewer.setSwapBlackAndWhite(appearance.swapBlackAndWhite);
      }
      if ("ghostHidden" in appearance && appearance.ghostHidden !== prefs.get('ghosting')) {
        is3d && this._viewer.setGhosting(appearance.ghostHidden);
      }
      if ("displayLines" in appearance && appearance.displayLines !== prefs.get('lineRendering')) {
        this._viewer.hideLines(!appearance.displayLines);
      }
      if ("displayPoints" in appearance && appearance.displayPoints !== prefs.get('pointRendering')) {
        this._viewer.hidePoints(!appearance.displayPoints);
      }
    }

    // SAO and AA at the end.
    if (is3d && saoEnabled !== prefs.get('ambientShadows') && antiAliasing !== prefs.get('antialiasing')) {
      this._viewer.setQualityLevel(saoEnabled, antiAliasing);
    }
  }

  /**
   * Gets the environment identifier.
   *
   * @returns {string}
   * @private
   */
  getEnvironmentName() {
    var preset = LightPresets[this._viewer.impl.currentLightPreset()];
    if (!preset) {
      return 'none';
    }
    return preset.name;
  }

  /**
   * Restores camera states values back into the viewer.
   * We avoid using methods such as setViewFromCamera() because those make some
   * assumptions about the current state of the viewer. We need no such things.
   *
   * Note: Implementation based on Viewer3DImpl.setViewFromCamera()
   *
   * @param {object} camera
   * @param {boolean} immediate
   * @private
   */
  restoreCameraState(camera, immediate) {

    this._viewer.impl.adjustOrthoCamera(camera);
    var navapi = this._viewer.navigation;

    if (!immediate) {
      // With animation
      this._viewer.impl.camera.isPerspective = camera.isPerspective;
      navapi.setRequestTransitionWithUp(true, camera.position, camera.target, camera.fov, camera.up, camera.worldup, camera.pivot);
    } else {
      // Instantaneous, no animation
      if (camera.isPerspective) {
        navapi.toPerspective();
      } else {
        navapi.toOrthographic();
      }
      navapi.setCameraUpVector(camera.up);
      navapi.setWorldUpVector(camera.worldup);
      navapi.setView(camera.position, camera.target);
      navapi.setPivotPoint(camera.pivot);
      navapi.setVerticalFov(camera.fov, false);

      this._viewer.impl.syncCamera(true);
    }
  }

  /**
   * Return true if two viewer states are equal, it's possible to compare only a subset of the objects providing a filter
   * as parameter.
   * @param {object} viewerStateA
   * @param {object} viewerStateB
   * @param {object} [filter] - Similar in structure to viewerState used to filter out values to check.
   * @returns {boolean} True if the states are equal.
   * @tutorial viewer_state
   */
  areEqual(viewerStateA, viewerStateB, filter) {

    const areArraysEqual = (arrayA, arrayB) => {

      arrayA = arrayA || [];
      arrayB = arrayB || [];

      if (arrayA.length !== arrayB.length) {
        return false;
      }

      for (var i = 0; i < arrayA.length; ++i) {
        if (arrayA[i] !== arrayB[i]) {
          return false;
        }
      }

      return true;
    };

    const areVectorsEqual = (vectorA, vectorB, epsilon) => {

      vectorA = vectorA || [];
      vectorB = vectorB || [];

      if (vectorA.length !== vectorB.length) {
        return false;
      }

      if (vectorA.length === 0) {
        return false;
      }

      return (
        areNumbersEqual(vectorA[0], vectorB[0], epsilon) ||
        areNumbersEqual(vectorA[1], vectorB[1], epsilon) ||
        areNumbersEqual(vectorA[2], vectorB[2], epsilon));

    };

    const areNumbersEqual = (numberA, numberB, epsilon) => {

      var parcedA = numberA ? parseFloat(numberA) : null;
      var parcedB = numberB ? parseFloat(numberB) : null;

      var typeOfA = typeof parcedA;
      var typeOfB = typeof parcedB;

      if (typeOfA === 'number' && typeOfB === 'number') {
        return Math.abs(numberA - numberB) < epsilon;
      }

      numberA = numberA ? numberA : null;
      numberB = numberB ? numberB : null;

      return numberA === numberB;
    };

    var stateA = viewerStateA;
    var stateB = viewerStateB;
    var epsilon = 0.000000001;

    if (filter && filter !== true) {
      stateA = this.applyFilter(stateA, filter);
      stateB = this.applyFilter(stateB, filter);
    }

    if (stateA["seedURN"] !== stateB["seedURN"]) {
      return false;
    }

    // Check object set (only check first element, the one written by ViewerState).
    var objectSetA = stateA["objectSet"] || [];
    var objectSetB = stateB["objectSet"] || [];

    if (objectSetA.length !== objectSetB.length) {
      return false;
    }

    var objectA = objectSetA[0] || {};
    var objectB = objectSetB[0] || {};

    if (
    objectA.idType !== objectB.idType ||
    !areNumbersEqual(objectA.explodeScale, objectB.explodeScale, epsilon) ||
    !areNumbersEqual(objectA.explodeOptions?.magnitude, objectB.explodeOptions?.magnitude, epsilon) ||
    !areNumbersEqual(objectA.explodeOptions?.depthDampening, objectB.explodeOptions?.depthDampening, epsilon) ||
    !areArraysEqual(objectA.id, objectB.id) ||
    !areArraysEqual(objectA.isolated, objectB.isolated) ||
    !areArraysEqual(objectA.hidden, objectB.hidden)) {
      return false;
    }

    // Check Viewport.
    var viewportA = stateA["viewport"] || {};
    var viewportB = stateB["viewport"] || {};

    if (viewportA["name"] !== viewportB["name"] ||
    viewportA["projection"] !== viewportB["projection"] ||
    viewportA["isOrthographic"] !== viewportB["isOrthographic"] ||
    !areNumbersEqual(viewportA["distanceToOrbit"], viewportB["distanceToOrbit"], epsilon) ||
    // !areNumbersEqual(viewportA["aspectRatio"], viewportB["aspectRatio"], epsilon) ||
    !areNumbersEqual(viewportA["fieldOfView"], viewportB["fieldOfView"], epsilon) ||
    !areNumbersEqual(viewportA["orthographicHeight"], viewportB["orthographicHeight"], epsilon) ||
    !areVectorsEqual(viewportA["eye"], viewportB["eye"], epsilon) ||
    !areVectorsEqual(viewportA["target"], viewportB["target"], epsilon) ||
    !areVectorsEqual(viewportA["up"], viewportB["up"], epsilon) ||
    !areVectorsEqual(viewportA["worldUpVector"], viewportB["worldUpVector"], epsilon) ||
    !areVectorsEqual(viewportA["pivotPoint"], viewportB["pivotPoint"], epsilon)) {
      return false;
    }

    // Skip render options, cut planes and extension data.
    return true;
  }

  /**
   * Helper method with the intent to change the type of an array with ids from String to ints.
   * We need this method because we need to make sure that ids that get fed into the ViewerState
   * are in the correct type.
   *
   * @param {Array} array - For example, `["45", "33", "1"]`.
   * @returns {Array} For example, `[45, 33, 1]`.
   * @private
   */
  toIntArray(array) {
    var ret = [];
    if (Array.isArray(array)) {
      for (var i = 0, len = array.length; i < len; ++i) {
        ret.push(parseInt(array[i]));
      }
    }
    return ret;
  }

  /**
   * Helper method that constructs a Vector3 from a given Array.
   * If Array is not well-formed, then the failValue is return instead.
   *
   * @param {array} array - An array with 3 values.
   * @param {THREE.Vector3} failValue - If array param is invalid, failValue will be returned instead.
   * @returns {THREE.Vector3} Either a new Vector with values coming from 'array' or failValue.
   * @private
   */
  getVector3FromArray(array, failValue) {

    if (array instanceof Array && array.length > 2) {

      // Some array values are exported as string-of-numbers. Fix that here.
      array[0] = parseFloat(array[0]);
      array[1] = parseFloat(array[1]);
      array[2] = parseFloat(array[2]);
      return new THREE.Vector3().fromArray(array);
    }
    return failValue;
  }

  /**
   * Helper method that construct a Quaternion from a given Array.
   * If Array is not well-formed, then the failValue is returned instead.
   *
   * @param {array} array - An array with 4 values.
   * @param {THREE.Quaternion} failValue - If array param is invalid, failValue will be returned instead.
   * @returns {THREE.Quaternion} - New Quaternion containing the values from 'array'.
   */
  getQuaternionFromArray(array, failValue) {
    if (array instanceof Array && array.length == 4) {
      array[0] = parseFloat(array[0]);
      array[1] = parseFloat(array[1]);
      array[2] = parseFloat(array[2]);
      array[3] = parseFloat(array[3]);
      return new THREE.Quaternion().fromArray(array);
    }
    return failValue;
  }

  /**
   * Helper function that returns selected node ids in an array.
   * @param {Model} [model] - model
   * @returns {array}
   * @private
   */
  getSelectedNodes(model) {
    model = model || this._viewer.model;
    return model.selector ? model.selector.getSelection() : [];
  }

  /**
   * Helper function that returns the index values of the isolated (visible) layers.
   * Applies only to 2d models/blueprints
   * @param {Autodesk.Viewing.Model} [model]
   * @private
   */
  getVisibleLayers2d(model) {
    if (!model || model === this._viewer.model) return this._viewer.impl.getVisibleLayerIndices();

    logger.warn('[getVisibleLayers2d] multiple models not yet supported.');
    return [];
  }

  /**
   * Helper function that returns true if all layers are visible.
   * Applies only to 2d models/blueprints
   * @param {Autodesk.Viewing.Model} [model]
   * @private
   */
  areAllLayersVisible(model) {
    if (!model || model === this._viewer.model) return this._viewer.impl.layers.allLayersVisible();

    logger.warn('[areAllLayersVisible] multiple models not yet supported.');
    // All of the layers should be visible
    return true;
  }

  /**
   * Gets the aspect ratio.
   * @returns {number} Aspect ratio.
   * @private
   */
  getAspectRatio() {
    var viewport = this._viewer.navigation.getScreenViewport();
    var aspect = viewport.width / viewport.height;
    return aspect;
  }

  /**
   * Returns world height when in orthographic camera mode.
   * @returns {number} Orthographic height.
   * @private
   */
  getOrthographicHeight() {
    var cam = this._viewer.navigation.getCamera();
    if (cam.isPerspective) return 0;
    let orthographicHeight = Math.abs(2 * cam.orthographicCamera.top);
    orthographicHeight = Math.max(orthographicHeight, 0.1);
    return orthographicHeight;
  }

  /**
   * Returns the URN of the document model.
   * @returns {string} Model URN.
   */
  getSeedUrn(model) {
    model = model || this._viewer.model;
    return model?.getSeedUrn() || "";
  }

  /**
   * Returns the slider value for the viewer's current light intensity.
   * @returns {number}
   * @private
   */
  getToneMapIntensity() {

    // Original code from RenderOptionsPanel.js
    // Should probably live elsewhere in the api.
    var intensity = 0.0;
    if (this._viewer.impl.dir_light1) {
      if (this._viewer.impl.dir_light1.intensity != 0)
      intensity = Math.log(this._viewer.impl.dir_light1.intensity) / Math.log(2.0);else

      intensity = -1e-20;
    }
    return intensity;
  }

  /**
   * Returns the index of the LightPreset with a matching name value.
   * @param environmentName
   * @returns {number} Index of LightPreset, or -1 if not found.
   * @private
   */
  getLightPresetIndex(environmentName) {

    for (var i = 0; i < LightPresets.length; i++) {
      if (LightPresets[i].name === environmentName) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Filters out key/value pairs from the viewerState.
   *
   * To get all of the values available use FILTER_ALL. If no filter is provided FILTER_ALL will be used.
   * It is encourage for consumers to define their specialized filters.
   *
   * @param {object} viewerState - Object to be filtered.
   * @param {object} filter - Object with a similar structure to viewerState, where values are Booleans signaling which
   * elements should be included (true) and which ones should not (false).
   * If a viewerState key is not found in the filter, we assume that it is non-wanted.
   * @private
   */
  applyFilter(viewerState, filter) {

    // Check the 'ALL' filter
    if (filter === true) return;

    // Filtering only 1 level depth keys
    // Additional levels are checked recursively.
    for (var key in viewerState) {

      if (!Object.prototype.hasOwnProperty.call(viewerState, key)) {
        continue;
      }

      // Try to find the key in the filter object
      var filterValue = filter[key];

      if (filterValue === undefined) {

        // key not enabled in filter, remove key/value pair from viewerState.
        delete viewerState[key];
      } else
      if (typeof filterValue === 'boolean') {

        if (filterValue === false) {
          // key explicitly flagged for removal, remove key/value pair from viewerState.
          delete viewerState[key];
        }
      } else
      if (filterValue instanceof Object) {

        if (viewerState[key] instanceof Object) {
          // Both are Objects, recursive call on them.
          this.applyFilter(viewerState[key], filter[key]);
        } else {
          // This case signals a miss-match between filter and value.
          // Since it's an undefined case, we'll be inclusive for the time being.
          // *** Keep the value in viewerState ***
          logger.warn("[applyFilter] A - Invalid filter Object for key [" + key + "]");
        }
      } else
      {

        // Note: Every other value for filter is invalid.
        // For now, we'll keep the key/value in viewerState.
        logger.warn("[applyFilter] B - Invalid filter value for key [" + key + "]");
      }

    }
  }

  /**
   * Converts viewport coordinates into range [0...1] using the model's bounding box.
   *
   * @param {object} viewerState - A state object obtained via `viewer.getState()`. The object is modified in-place.
   */
  normalizeCoordinates(viewerState) {

    var viewport = viewerState.viewport;
    if (viewport) {

      // Avoid operating on the object more than once
      if (viewport.normalized) {
        logger.warn('invalid normalization of state.viewport. Ignoring command.');
        return;
      }

      // Add flag
      viewport.normalized = true;

      const mat = SceneMath.getNormalizingMatrix(this._viewer.model);
      applyMatrix4(viewport, mat);

      // Change orthographicHeight as a ratio of the model's bounding-box height.
      if ('orthographicHeight' in viewport) {
        var bbox = this._viewer.model.myData.bbox;
        var bboxHeight = bbox.max.y - bbox.min.y;
        var newValue = viewport.orthographicHeight / bboxHeight;
        viewport.orthographicHeight = newValue;
      }
    }
  }

  /**
   * Converts normalized coordinates [0..1] into bounding box coordinates.
   *
   * @param {object} viewerState - A state object obtained via `viewer.getState()`. The object is modified in-place.
   */
  denormalizeCoordinates(viewerState) {

    var viewport = viewerState.viewport;
    if (viewport) {

      // Make sure data is normalized
      if (!viewport.normalized) {
        logger.warn('invalid denormalization of state.viewport. Ignoring command.');
        return;
      }

      // Remove flag
      delete viewport.normalized;

      const mat = SceneMath.getNormalizingMatrix(this._viewer.model);
      let matInv = mat.clone().invert();
      applyMatrix4(viewport, matInv);

      // Restore orthographicHeight from a ratio of the model's bounding-box height.
      if ('orthographicHeight' in viewport) {
        var bbox = this._viewer.model.myData.bbox;
        var bboxHeight = bbox.max.y - bbox.min.y;
        var newValue = viewport.orthographicHeight * bboxHeight;
        viewport.orthographicHeight = newValue;
      }
    }
  }

}

function applyMatrix4(viewport, matrix) {
  var eye = new THREE.Vector3(viewport.eye[0], viewport.eye[1], viewport.eye[2]);
  var pivot = new THREE.Vector3(viewport.pivotPoint[0], viewport.pivotPoint[1], viewport.pivotPoint[2]);
  var target = new THREE.Vector3(viewport.target[0], viewport.target[1], viewport.target[2]);

  eye.applyMatrix4(matrix);
  pivot.applyMatrix4(matrix);
  target.applyMatrix4(matrix);

  viewport.eye[0] = eye.x;
  viewport.eye[1] = eye.y;
  viewport.eye[2] = eye.z;
  viewport.pivotPoint[0] = pivot.x;
  viewport.pivotPoint[1] = pivot.y;
  viewport.pivotPoint[2] = pivot.z;
  viewport.target[0] = target.x;
  viewport.target[1] = target.y;
  viewport.target[2] = target.z;
}